import {Simplex as BaseSimplex} from 'services/sports/default/simplex';
import Action from 'actions';
import ArrayHelper, {groupObjectsByProp} from 'helpers/array-helper';
import RuleBuilder from 'services/simplex/rule-builder';
import {getSimplexUnitName} from 'helpers/simplex-helper';
import Const from 'constants';

export default class SimplexClassic extends BaseSimplex {
    maxPlayersFromTeam({}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersGroupedByAllowedPositions = preparedPlayers.groupedBy.allowedPositions();
        const teams = groupObjectsByProp(playersGroupedByAllowedPositions, 'team');
        const maxSingleTeamPlayers = sportConfig.getConfigProp('maxSingleTeamPlayers');

        const rules = [];
        Object.values(teams).map((players) => {
            const ruleBuilder = RuleBuilder.fromAtMost(maxSingleTeamPlayers);

            const addedPlayers = [];
            players.map((player) => {
                if (!inArray(player.playerId, addedPlayers)) {
                    ruleBuilder.addVar(getSimplexUnitName(player.playerId), 1);
                    addedPlayers.push(player.playerId);
                }
            });

            if (ruleBuilder.isRuleCreatable()) {
                rules.push(ruleBuilder.createRule());
            }
        });

        return [...rules];
    }

    minTeams({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersGroupedByAllowedPositions = preparedPlayers.groupedBy.allowedPositions();
        const teams = groupObjectsByProp(playersGroupedByAllowedPositions, 'team');

        const simplexUnitNamesByTeams = {};
        const teamRows = {};
        const couples = [];
        const rules = [];
        const maxPlayersInTeam = sportConfig.getConfigProp('playersInTeam') - 1;
        const minTeams = Number(sportConfig.getConfigProp('minTeams'));

        Object.keys(teams).map((teamName) => {
            const addedPlayers = [];
            teamRows[teamName] = {};
            simplexUnitNamesByTeams[teamName] = {};
            teams[teamName].map((player) => {
                if (!inArray(player.playerId, addedPlayers)) {
                    const simplexUnitName = getSimplexUnitName(player.playerId);
                    teamRows[teamName] += '+' + simplexUnitName;
                    simplexUnitNamesByTeams[teamName][simplexUnitName] = 1;
                    addedPlayers.push(player.playerId);
                }
            });
        });

        if (ArrayHelper.countItems(teams) > minTeams) {
            const addedCombos = [];
            Object.keys(teamRows).map((tn1) => {
                Object.keys(teamRows).map((tn2) => {
                    if (tn1 !== tn2) {
                        let combo1 = tn1 + tn2,
                            combo2 = tn2 + tn1;
                        if (!inArray(combo1, addedCombos) && !inArray(combo2, addedCombos)) {
                            couples.push({
                                ...simplexUnitNamesByTeams[tn1],
                                ...simplexUnitNamesByTeams[tn2],
                            });
                            addedCombos.push(combo1);
                        }
                    }
                });
            });
            Object.values(couples).map((simplexUnitNames) => {
                const ruleBuilder = RuleBuilder.fromAtMost(maxPlayersInTeam);
                ruleBuilder.addVars(simplexUnitNames);
                rules.push(ruleBuilder.createRule());
            });
        } else {
            Object.values(simplexUnitNamesByTeams).map((simplexUnitNames) => {
                const ruleBuilder = RuleBuilder.fromAtLeast(1);
                ruleBuilder.addVars(simplexUnitNames);
                rules.push(ruleBuilder.createRule());
            });
        }

        return [...rules];
    }

    flexMaxExposure({}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();
        const forbiddenSuperFlexPositions = this.exec('getForbiddenSuperFlexMaxExposurePositions');

        const flexPositions = sportConfig.getConfigProp('flexPlayerPosition');
        const positions = sportConfig.getConfigProp('positions');
        const superFlexPosition = positions[flexPositions[Const.POSITION_SUPERFLEX]];

        let decrementFlex = false;
        let decrementSuperFlex = false;

        const rules = [];
        Object.keys(positions).map((pos) => {
            let playersOnPos = {};
            let addedPlayers = [];
            let posObj = positions[pos];
            let isPositionFlex = posObj.id === 'flex';

            players.map((player) => {
                if (!inArray(player.position, posObj.allowedPositions) || inArray(player.playerId, addedPlayers)) {
                    return;
                }

                playersOnPos[player.team] ??= [];
                playersOnPos[player.team].push(player.playerId);

                addedPlayers.push(player.playerId);
            });

            if (isEmpty(playersOnPos)) {
                return;
            }

            const row = {};
            Object.values(playersOnPos).map((playerIds) => {
                if (posObj.maxFromOneTeam) {
                    const ruleBuilder = RuleBuilder.fromAtMost(posObj.maxFromOneTeam);
                    playerIds.map((playerId) => ruleBuilder.addVar(getSimplexUnitName(playerId), 1));
                    rules.push(ruleBuilder.createRule());
                } else {
                    playerIds.map((playerId) => (row[getSimplexUnitName(playerId)] = 1));
                }
            });

            if (!isEmpty(row)) {
                let forbiddenMaxPlayersQuantity = 0;
                let forbiddenMinPlayersQuantity = 0;

                if (!isPositionFlex && !isEmpty(forbiddenSuperFlexPositions)) {
                    let countSuperFlex = ArrayHelper.countItems(forbiddenSuperFlexPositions);
                    if (inArray(posObj.shortName, Object.keys(forbiddenSuperFlexPositions))) {
                        forbiddenMaxPlayersQuantity += forbiddenSuperFlexPositions[posObj.shortName];
                        //case when all value are 0
                        if (superFlexPosition.allowedPositions.length === countSuperFlex) {
                            decrementSuperFlex = true;
                        }
                    } else if (
                        superFlexPosition.allowedPositions.length - 1 === countSuperFlex &&
                        arrayDuplicates(posObj.allowedPositions, superFlexPosition.allowedPositions).length > 0
                    ) {
                        forbiddenMinPlayersQuantity++;
                    }
                }

                if (isPositionFlex && (decrementFlex || decrementSuperFlex)) {
                    forbiddenMaxPlayersQuantity++;
                }

                if (Boolean(posObj.minPlayers)) {
                    const ruleBuilder = RuleBuilder.fromAtLeast(posObj.minPlayers + forbiddenMinPlayersQuantity);
                    ruleBuilder.addVars(row);
                    rules.push(ruleBuilder.createRule());
                }
                if (Boolean(posObj.maxPlayers)) {
                    const ruleBuilder = RuleBuilder.fromAtMost(posObj.maxPlayers - forbiddenMaxPlayersQuantity);
                    ruleBuilder.addVars(row);
                    rules.push(ruleBuilder.createRule());
                }
            }
        });

        return [...rules];
    }

    getForbiddenSuperFlexMaxExposurePositions({}) {
        const {
            lineupsQuantity,
            playersData: {superFlexMaxExposurePositions},
        } = Action.getState();

        if (isEmpty(superFlexMaxExposurePositions)) {
            return {};
        }

        const usedPlayers = this.store.get('usedPlayers', []);
        const flexPositionStats = this.store.get('flexPositionStats', {});

        const result = {};

        if (!isEmpty(usedPlayers)) {
            const flexPositions = this.exec('defineFlexPositionsInLineupByPositionsOrder', {
                lineup: usedPlayers[usedPlayers.length - 1],
            });
            flexPositions.map((flexPosition) => {
                flexPositionStats[flexPosition] ??= 0;
                flexPositionStats[flexPosition]++;
            });
            this.store.set('flexPositionStats', flexPositionStats);
        }

        Object.keys(superFlexMaxExposurePositions).map((posName) => {
            const usageQuantity = superFlexMaxExposurePositions[posName];

            if (Const.DEFAULT_VALUE_FOR_MAX_EXPOSURE > usageQuantity) {
                const usedInLineups = flexPositionStats[posName] ?? 0;

                let usedInLineupsPercentage = (1 / lineupsQuantity.getLineupsQuantity()) * 100;
                if (usedInLineups > 0) {
                    usedInLineupsPercentage = (usedInLineups / lineupsQuantity.getLineupsQuantity()) * 100;
                }

                if (usedInLineupsPercentage >= usageQuantity || usageQuantity === 0) {
                    result[posName] = 1;
                }
            }
        });

        return {...result};
    }

    defineFlexPositionsInLineupByPositionsOrder({incomingValues: {lineup}}) {
        let playerIds = [...lineup];
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.playerId();
        const positionsOrder = sportConfig.getConfigProp('positionsOrder', {});

        Object.values(positionsOrder).map((position) => {
            for (let key in playerIds) {
                const playerId = playerIds[key];
                if (position.alias === players[playerId].positionName) {
                    playerIds.splice(key, 1);
                    break;
                }
            }
        });

        return playerIds.map((playerId) => players[playerId].positionName);
    }

    subjectToPermanent() {
        const constraints = {
            player: this.exec('player', {}, []),
            salary: this.exec('salary', {}, []),
            maxPlayersFromTeam: this.exec('maxPlayersFromTeam', {}, []),
            minTeams: this.exec('minTeams', {}, ''),
            keyPlayerStacking: this.exec('keyPlayerStacking', {}, []),
            teamNameStacking: this.exec('teamNameStacking', {}, []),
            passReceiver: this.exec('passReceiver', {}, []),
            projectionBoostRule: this.exec('projectionBoostRule', {}, []),
            bound: this.exec('bound', {}, []),
        };

        return {...constraints};
    }

    subjectToAlterable() {
        const constraints = {
            playersWhoseGamesAreLocked: this.exec('playersWhoseGamesAreLocked', {}, []),
            createdLineups: this.exec('createdLineups', {}, []),
            exposureMax: this.exec('exposureMax', {}, []),
            exposureMin: this.exec('exposureMin', {}, []),
            flexExposureMax: this.exec('flexMaxExposure', {}, []),
            playerCombinationStacking: this.exec('playerCombinationStacking', {}, []),
        };

        return {...constraints};
    }
}
